import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { DateLisible, Nombre } from '../components/Nombre'
import { Badge, Button, Card, CheckboxField, DataTable, FormActions, Grid, PageHeader, Stack, TextField, useToast } from '../components/ui'
import type { Column } from '../components/ui'
import { growthApi } from './growthApi'
import type { DiscountCode } from './growthApi'

const entierOuNul = (s: string) => (s.trim() === '' ? null : Number(s))

/**
 * Les codes de remise (migration 0081). Un pourcentage de 1 à 99, une durée en périodes (vide :
 * tant que l'abonnement vit), une date limite d'application, un nombre maximal d'utilisations.
 *
 * ENREGISTRER UN CODE EXISTANT LE MET À JOUR, sans toucher ce qui a déjà été accordé : le
 * taux est recopié dans l'abonnement au moment où la remise est appliquée.
 */
export function DiscountCodesPage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const token = session?.token ?? ''

  const [codes, setCodes] = useState<DiscountCode[]>([])
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [pourcentage, setPourcentage] = useState('')
  const [duree, setDuree] = useState('')
  const [limite, setLimite] = useState('')
  const [maximum, setMaximum] = useState('')
  const [actif, setActif] = useState(true)
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    if (token === '') return
    growthApi.listCodes(token).then(setCodes).catch((cause: unknown) => notify(describeError(cause), 'erreur'))
  }, [token, notify, describeError])

  useEffect(charger, [charger])

  const editer = (c: DiscountCode) => {
    setCode(c.code)
    setDescription(c.description ?? '')
    setPourcentage(String(c.percentOff))
    setDuree(c.durationCycles === null ? '' : String(c.durationCycles))
    setLimite(c.validUntil ?? '')
    setMaximum(c.maxRedemptions === null ? '' : String(c.maxRedemptions))
    setActif(c.isActive)
  }

  const enregistrer = async () => {
    setEnvoi(true)
    try {
      await growthApi.saveCode(token, {
        code: code.trim(),
        description: description.trim() === '' ? null : description.trim(),
        percentOff: Number(pourcentage.replace(',', '.')),
        durationCycles: entierOuNul(duree),
        validUntil: limite === '' ? null : limite,
        maxRedemptions: entierOuNul(maximum),
        isActive: actif,
      })
      notify(t('growth.codes.saved'), 'succes')
      setCode('')
      setDescription('')
      setPourcentage('')
      setDuree('')
      setLimite('')
      setMaximum('')
      setActif(true)
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const colonnes: Column<DiscountCode>[] = [
    { key: 'code', header: t('growth.codes.code'), render: (c) => <span dir="ltr" className="font-medium">{c.code}</span> },
    { key: 'percent', header: t('growth.codes.percent'), numeric: true, render: (c) => <Nombre valeur={c.percentOff} decimales={0} unite="%" /> },
    { key: 'duration', header: t('growth.codes.duration'), render: (c) => (c.durationCycles === null ? t('growth.codes.forever') : <Nombre valeur={c.durationCycles} decimales={0} />) },
    { key: 'until', header: t('growth.codes.validUntil'), render: (c) => (c.validUntil === null ? '—' : <DateLisible valeur={c.validUntil} />), secondary: true },
    {
      key: 'uses',
      header: t('growth.codes.uses'),
      numeric: true,
      render: (c) => (
        <>
          <Nombre valeur={c.redemptions} decimales={0} />
          {c.maxRedemptions !== null && <> / <Nombre valeur={c.maxRedemptions} decimales={0} /></>}
        </>
      ),
    },
    { key: 'active', header: t('billing.col.status'), render: (c) => <Badge tone={c.isActive ? 'succes' : 'info'}>{t(c.isActive ? 'common.active' : 'growth.codes.inactive')}</Badge> },
    { key: 'edit', header: '', render: (c) => <Button variant="discret" onClick={() => editer(c)}>{t('growth.codes.edit')}</Button> },
  ]

  return (
    <Stack>
      <PageHeader title={t('growth.codes.title')} description={t('growth.codes.description')} />
      <Card title={t('growth.codes.list')}>
        <DataTable columns={colonnes} rows={codes} rowKey={(c) => c.id} emptyLabel={t('growth.codes.empty')} />
      </Card>
      <Card title={t('growth.codes.form')}>
        <Stack gap={3}>
          <Grid min="12rem">
            <TextField label={t('growth.codes.code')} value={code} onChange={(v) => setCode(v.toUpperCase())} ltr required />
            <TextField label={t('growth.codes.percent')} value={pourcentage} onChange={setPourcentage} ltr required hint={t('growth.codes.percentHint')} />
            <TextField label={t('growth.codes.duration')} value={duree} onChange={setDuree} ltr hint={t('growth.codes.durationHint')} />
            <TextField label={t('growth.codes.validUntil')} type="date" value={limite} onChange={setLimite} />
            <TextField label={t('growth.codes.max')} value={maximum} onChange={setMaximum} ltr />
          </Grid>
          <TextField label={t('growth.codes.descriptionLabel')} value={description} onChange={setDescription} />
          <CheckboxField label={t('common.active')} checked={actif} onChange={setActif} />
          <FormActions>
            <Button onClick={enregistrer} disabled={code.trim() === '' || pourcentage.trim() === ''} pending={envoi}>
              {t('common.save')}
            </Button>
          </FormActions>
        </Stack>
      </Card>
    </Stack>
  )
}
